import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, message, Tag, Tabs, Row, Col, DatePicker, Alert, Progress, Tooltip, Upload, List, Space } from 'antd';
import { PlusOutlined, WarningOutlined, SafetyCertificateOutlined, UploadOutlined, FileOutlined, DeleteOutlined, EyeOutlined, EditOutlined } from '@ant-design/icons';
import ApiService from '../services/api';
import dayjs from 'dayjs';

const { Option } = Select;
const { TabPane } = Tabs;

const DOCUMENT_TYPES = [
  { key: 'class2_license', label: 'Class 2 License', required: true },
  { key: 'ddc', label: 'Defensive Driving Certificate', required: true },
  { key: 'medical', label: 'Medical Certificate', required: true },
  { key: 'police_clearance', label: 'Police Clearance', required: true },
  { key: 'passport', label: 'Passport', required: false },
  { key: 'other', label: 'Other Documents', required: false }
];

const Drivers = () => {
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [complianceModal, setComplianceModal] = useState(false);
  const [filesModal, setFilesModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [complianceForm] = Form.useForm();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const data = await ApiService.getDashboardData();
      setDrivers(data.drivers || []);
      setTrucks(data.trucks || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDriver = async (values) => {
    try {
      const driverCount = drivers.length;
      const driverId = `DRV${String(driverCount + 1).padStart(3, '0')}`;
      
      const driverData = {
        ...values,
        driver_id: driverId,
        date_of_birth: values.date_of_birth?.format('YYYY-MM-DD'),
        hire_date: values.hire_date?.format('YYYY-MM-DD'),
        compliance: {
          class2_license: {
            number: values.license_number,
            expiry: values.license_expiry?.format('YYYY-MM-DD'),
            retest_date: values.retest_date?.format('YYYY-MM-DD')
          },
          defensive_driving: {
            number: values.ddc_number,
            expiry: values.ddc_expiry?.format('YYYY-MM-DD')
          },
          medical_certificate: {
            expiry: values.medical_expiry?.format('YYYY-MM-DD')
          },
          passport: {
            number: values.passport_number,
            expiry: values.passport_expiry?.format('YYYY-MM-DD')
          },
          police_clearance: {
            date: values.police_clearance_date?.format('YYYY-MM-DD')
          }
        }
      };
      
      await ApiService.addDriver(driverData);
      message.success('Driver added successfully');
      setModalVisible(false);
      form.resetFields();
      loadData();
    } catch (error) {
      message.error(`Error adding driver: ${error.message}`);
    }
  };

  const handleUpdateCompliance = async (values) => {
    try {
      const complianceData = {
        compliance: {
          class2_license: {
            number: values.license_number,
            expiry: values.license_expiry?.format('YYYY-MM-DD'),
            retest_date: values.retest_date?.format('YYYY-MM-DD')
          },
          defensive_driving: {
            number: values.ddc_number,
            expiry: values.ddc_expiry?.format('YYYY-MM-DD')
          },
          medical_certificate: {
            expiry: values.medical_expiry?.format('YYYY-MM-DD')
          },
          passport: {
            number: values.passport_number,
            expiry: values.passport_expiry?.format('YYYY-MM-DD')
          },
          police_clearance: {
            date: values.police_clearance_date?.format('YYYY-MM-DD')
          },
          years_experience: values.years_experience,
          age: values.age
        }
      };
      
      await ApiService.updateDriver(selectedDriver.driver_id, complianceData);
      message.success('Compliance updated');
      setComplianceModal(false);
      loadData();
    } catch (error) {
      message.error(`Error: ${error.message}`);
    }
  };

  const handleEditDriver = async (values) => {
    try {
      const updateData = {
        name: values.name,
        phone: values.phone,
        email: values.email,
        national_id: values.national_id,
        date_of_birth: values.date_of_birth?.format('YYYY-MM-DD'),
        hire_date: values.hire_date?.format('YYYY-MM-DD'),
        license_number: values.license_number
      };
      
      await ApiService.updateDriver(selectedDriver.driver_id, updateData);
      message.success('Driver info updated');
      setEditModal(false);
      loadData();
    } catch (error) {
      message.error(`Error: ${error.message}`);
    }
  };

  const openEditModal = (driver) => {
    setSelectedDriver(driver);
    editForm.setFieldsValue({
      name: driver.name,
      phone: driver.phone,
      email: driver.email,
      national_id: driver.national_id,
      date_of_birth: driver.date_of_birth ? dayjs(driver.date_of_birth) : null,
      hire_date: driver.hire_date ? dayjs(driver.hire_date) : null,
      license_number: driver.license_number || driver.compliance?.class2_license?.number
    });
    setEditModal(true);
  };

  const handleFileUpload = async (file, documentType) => {
    if (!selectedDriver) return false;
    
    setUploading(true);
    try {
      // Get presigned URL
      const response = await ApiService.generateDriverUploadUrl(
        selectedDriver.driver_id,
        file.name,
        file.type,
        documentType
      );
      
      // Upload to S3
      await fetch(response.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type }
      });
      
      // Update driver documents list
      const currentDocs = selectedDriver.documents || [];
      const newDoc = {
        type: documentType,
        fileName: file.name,
        fileUrl: response.fileUrl,
        fileKey: response.fileKey,
        uploadedAt: new Date().toISOString()
      };
      
      const updatedDocs = [...currentDocs, newDoc];
      await ApiService.updateDriverDocuments(selectedDriver.driver_id, updatedDocs);
      
      // Update local state
      setSelectedDriver({ ...selectedDriver, documents: updatedDocs });
      message.success(`${file.name} uploaded successfully`);
      loadData();
    } catch (error) {
      message.error(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
    return false; // Prevent default upload
  };

  const handleDeleteDocument = async (docIndex) => {
    if (!selectedDriver) return;
    
    try {
      const updatedDocs = selectedDriver.documents.filter((_, idx) => idx !== docIndex);
      await ApiService.updateDriverDocuments(selectedDriver.driver_id, updatedDocs);
      setSelectedDriver({ ...selectedDriver, documents: updatedDocs });
      message.success('Document removed');
      loadData();
    } catch (error) {
      message.error(`Error: ${error.message}`);
    }
  };

  const handleAssociateTruck = async (driverId, truckId) => {
    try {
      await ApiService.associateDriverTruck(driverId, truckId);
      message.success('Truck assigned');
      loadData();
    } catch (error) {
      message.error('Error assigning truck');
    }
  };

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return { status: 'missing', color: 'default', text: 'Not Set' };
    const today = dayjs();
    const expiry = dayjs(expiryDate);
    const daysUntil = expiry.diff(today, 'day');
    
    if (daysUntil < 0) return { status: 'expired', color: 'red', text: 'Expired' };
    if (daysUntil <= 30) return { status: 'critical', color: 'red', text: `${daysUntil}d left` };
    if (daysUntil <= 60) return { status: 'warning', color: 'orange', text: `${daysUntil}d left` };
    if (daysUntil <= 90) return { status: 'soon', color: 'gold', text: `${daysUntil}d left` };
    return { status: 'valid', color: 'green', text: 'Valid' };
  };

  const getComplianceScore = (driver) => {
    const c = driver.compliance || {};
    let score = 0;
    let total = 4; // Passport not mandatory
    
    if (c.class2_license?.expiry && getExpiryStatus(c.class2_license.expiry).status === 'valid') score++;
    if (c.defensive_driving?.expiry && getExpiryStatus(c.defensive_driving.expiry).status === 'valid') score++;
    if (c.medical_certificate?.expiry && getExpiryStatus(c.medical_certificate.expiry).status === 'valid') score++;
    if (c.police_clearance?.date) {
      const clearanceAge = dayjs().diff(dayjs(c.police_clearance.date), 'month');
      if (clearanceAge <= 6) score++;
    }
    
    return Math.round((score / total) * 100);
  };

  const openComplianceModal = (driver) => {
    setSelectedDriver(driver);
    const c = driver.compliance || {};
    complianceForm.setFieldsValue({
      license_number: c.class2_license?.number || driver.license_number,
      license_expiry: c.class2_license?.expiry ? dayjs(c.class2_license.expiry) : null,
      retest_date: c.class2_license?.retest_date ? dayjs(c.class2_license.retest_date) : null,
      ddc_number: c.defensive_driving?.number,
      ddc_expiry: c.defensive_driving?.expiry ? dayjs(c.defensive_driving.expiry) : null,
      medical_expiry: c.medical_certificate?.expiry ? dayjs(c.medical_certificate.expiry) : null,
      passport_number: c.passport?.number,
      passport_expiry: c.passport?.expiry ? dayjs(c.passport.expiry) : null,
      police_clearance_date: c.police_clearance?.date ? dayjs(c.police_clearance.date) : null,
      years_experience: c.years_experience,
      age: c.age
    });
    setComplianceModal(true);
  };

  const openFilesModal = (driver) => {
    setSelectedDriver(driver);
    setFilesModal(true);
  };

  const getExpiringDocuments = () => {
    const alerts = [];
    drivers.forEach(driver => {
      const c = driver.compliance || {};
      const docs = [
        { name: 'Class 2 License', expiry: c.class2_license?.expiry },
        { name: 'Defensive Driving', expiry: c.defensive_driving?.expiry },
        { name: 'Medical Certificate', expiry: c.medical_certificate?.expiry }
      ];
      docs.forEach(doc => {
        const status = getExpiryStatus(doc.expiry);
        if (status.status === 'expired' || status.status === 'critical' || status.status === 'warning') {
          alerts.push({ driver: driver.name, document: doc.name, status, expiry: doc.expiry });
        }
      });
    });
    return alerts.sort((a, b) => dayjs(a.expiry).diff(dayjs(b.expiry)));
  };

  const expiringDocs = getExpiringDocuments();

  const columns = [
    { 
      title: 'Driver', 
      key: 'driver',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.name}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{record.phone}</div>
        </div>
      )
    },
    { 
      title: 'License', 
      dataIndex: 'license_number', 
      key: 'license',
      render: (license, record) => {
        const c = record.compliance?.class2_license || {};
        const status = getExpiryStatus(c.expiry);
        return (
          <div>
            <div>{license || c.number || '-'}</div>
            <Tag color={status.color} style={{ fontSize: 10 }}>{status.text}</Tag>
          </div>
        );
      }
    },
    {
      title: 'Compliance',
      key: 'compliance',
      render: (_, record) => {
        const score = getComplianceScore(record);
        return (
          <Tooltip title={`${score}% compliant`}>
            <Progress 
              percent={score} 
              size="small" 
              status={score < 50 ? 'exception' : score < 80 ? 'normal' : 'success'}
              style={{ width: 80 }}
            />
          </Tooltip>
        );
      }
    },
    {
      title: 'Documents',
      key: 'documents',
      render: (_, record) => {
        const c = record.compliance || {};
        const docCount = record.documents?.length || 0;
        return (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            <Tooltip title={`DDC: ${getExpiryStatus(c.defensive_driving?.expiry).text}`}>
              <Tag color={getExpiryStatus(c.defensive_driving?.expiry).color}>DDC</Tag>
            </Tooltip>
            <Tooltip title={`Medical: ${getExpiryStatus(c.medical_certificate?.expiry).text}`}>
              <Tag color={getExpiryStatus(c.medical_certificate?.expiry).color}>MED</Tag>
            </Tooltip>
            {docCount > 0 && <Tag color="blue">{docCount} files</Tag>}
          </div>
        );
      }
    },
    {
      title: 'Truck',
      dataIndex: 'truck_id',
      key: 'truck_id',
      render: (truckId, record) => {
        const truck = trucks.find(t => t.truck_id === truckId);
        return truck ? (
          <Tag color="blue">{truck.license_plate}</Tag>
        ) : (
          <Select
            placeholder="Assign"
            size="small"
            style={{ width: 100 }}
            onChange={(value) => handleAssociateTruck(record.driver_id, value)}
          >
            {trucks.filter(t => !t.driver_id).map(t => (
              <Option key={t.truck_id} value={t.truck_id}>{t.license_plate}</Option>
            ))}
          </Select>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 240,
      render: (_, record) => (
        <Space size="small">
          <Button 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          >
            Edit
          </Button>
          <Button 
            size="small" 
            icon={<SafetyCertificateOutlined />}
            onClick={() => openComplianceModal(record)}
          >
            Compliance
          </Button>
          <Button 
            size="small" 
            icon={<FileOutlined />}
            onClick={() => openFilesModal(record)}
          >
            Files
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1>Drivers Management</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>
          Add Driver
        </Button>
      </div>

      {expiringDocs.length > 0 && (
        <Alert
          message={`${expiringDocs.length} Document Alert${expiringDocs.length > 1 ? 's' : ''}`}
          description={
            <div style={{ maxHeight: 100, overflow: 'auto' }}>
              {expiringDocs.slice(0, 5).map((alert, idx) => (
                <div key={idx} style={{ fontSize: 12 }}>
                  <WarningOutlined style={{ color: alert.status.color, marginRight: 4 }} />
                  <strong>{alert.driver}</strong>: {alert.document} - {alert.status.text}
                </div>
              ))}
              {expiringDocs.length > 5 && <div style={{ fontSize: 12, color: '#666' }}>...and {expiringDocs.length - 5} more</div>}
            </div>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card>
        <Table
          dataSource={drivers}
          columns={columns}
          loading={loading}
          rowKey="driver_id"
          size="middle"
        />
      </Card>

      {/* Add Driver Modal */}
      <Modal
        title="Add New Driver"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={700}
      >
        <Form form={form} onFinish={handleAddDriver} layout="vertical">
          <Tabs defaultActiveKey="basic">
            <TabPane tab="Basic Info" key="basic">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="name" label="Full Name" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="date_of_birth" label="Date of Birth">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="national_id" label="National ID">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="hire_date" label="Hire Date">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </TabPane>
            <TabPane tab="Licenses & Certificates" key="licenses">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="license_number" label="Class 2 License Number" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="license_expiry" label="License Expiry">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="retest_date" label="Retest Date">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="ddc_number" label="Defensive Driving Certificate #">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="ddc_expiry" label="DDC Expiry">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="medical_expiry" label="Medical Certificate Expiry">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </TabPane>
            <TabPane tab="Other Documents" key="other">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="passport_number" label="Passport Number (Optional)">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="passport_expiry" label="Passport Expiry">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="police_clearance_date" label="Police Clearance Date">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </TabPane>
          </Tabs>
        </Form>
      </Modal>

      {/* Edit Driver Modal */}
      <Modal
        title={<span><EditOutlined /> Edit Driver - {selectedDriver?.name}</span>}
        open={editModal}
        onCancel={() => setEditModal(false)}
        onOk={() => editForm.submit()}
        width={600}
      >
        <Form form={editForm} onFinish={handleEditDriver} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="Full Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="national_id" label="National ID">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="date_of_birth" label="Date of Birth">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="hire_date" label="Hire Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="license_number" label="Driver's License Number">
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Compliance Modal */}
      <Modal
        title={<span><SafetyCertificateOutlined /> {selectedDriver?.name} - Compliance</span>}
        open={complianceModal}
        onCancel={() => setComplianceModal(false)}
        onOk={() => complianceForm.submit()}
        width={700}
      >
        <Form form={complianceForm} onFinish={handleUpdateCompliance} layout="vertical">
          <Alert 
            message="Zimbabwe VID Requirements" 
            description="Class 2 License, DDC, Medical Certificate, and Police Clearance are mandatory. Passport is optional."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Tabs defaultActiveKey="license">
            <TabPane tab="Class 2 License" key="license">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="license_number" label="License Number">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="license_expiry" label="Expiry Date">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="retest_date" label="Retest Date">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </TabPane>
            <TabPane tab="DDC & Medical" key="ddc">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="ddc_number" label="DDC Number">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="ddc_expiry" label="DDC Expiry">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="medical_expiry" label="Medical Certificate Expiry">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </TabPane>
            <TabPane tab="Passport & Clearance" key="passport">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="passport_number" label="Passport Number (Optional)">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="passport_expiry" label="Passport Expiry">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="police_clearance_date" label="Police Clearance Date">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </TabPane>
            <TabPane tab="Experience" key="experience">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="years_experience" label="Years of Experience">
                    <Input type="number" min={0} addonAfter="years" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="age" label="Age">
                    <Input type="number" min={25} max={65} addonAfter="years" />
                  </Form.Item>
                </Col>
              </Row>
              <Alert 
                message="VID Requirements: Min 5 years experience, Age 25-65" 
                type="info" 
                style={{ marginTop: 8 }}
              />
            </TabPane>
          </Tabs>
        </Form>
      </Modal>

      {/* Files Upload Modal */}
      <Modal
        title={<span><FileOutlined /> {selectedDriver?.name} - Document Files</span>}
        open={filesModal}
        onCancel={() => setFilesModal(false)}
        footer={<Button onClick={() => setFilesModal(false)}>Close</Button>}
        width={600}
      >
        <Alert 
          message="Upload compliance documents (PDF or images)" 
          type="info" 
          showIcon 
          style={{ marginBottom: 16 }}
        />
        
        {DOCUMENT_TYPES.map(docType => (
          <div key={docType.key} style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontWeight: 500 }}>
                {docType.label} {docType.required && <span style={{ color: 'red' }}>*</span>}
              </span>
              <Upload
                accept=".pdf,.jpg,.jpeg,.png"
                showUploadList={false}
                beforeUpload={(file) => handleFileUpload(file, docType.key)}
                disabled={uploading}
              >
                <Button size="small" icon={<UploadOutlined />} loading={uploading}>
                  Upload
                </Button>
              </Upload>
            </div>
            
            {/* Show uploaded files for this type */}
            {selectedDriver?.documents?.filter(d => d.type === docType.key).map((doc, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px', background: '#fff', borderRadius: 4, marginTop: 4 }}>
                <span style={{ fontSize: 12 }}>{doc.fileName}</span>
                <Space size="small">
                  <Button 
                    type="link" 
                    size="small" 
                    icon={<EyeOutlined />}
                    onClick={() => window.open(doc.fileUrl, '_blank')}
                  />
                  <Button 
                    type="link" 
                    size="small" 
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteDocument(selectedDriver.documents.indexOf(doc))}
                  />
                </Space>
              </div>
            ))}
          </div>
        ))}
      </Modal>
    </div>
  );
};

export default Drivers;
